import React, { FC, useCallback, useEffect, useState } from 'react'
import QRCode from 'react-qr-code'
import { useSelector } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { ISfWallet } from '@sparling/financial-common'
import { Grid, IconButton, Typography, Box } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { InputSelect } from '../../../../../../../components/Dashboard/InputSelect'
import { icons } from '../../../../../../../constants/icons'
import { Loader } from '../../../../../../../components/Loader'
import { isLoadingUserWalletsListSelector, userSfWalletsSelector } from '../../../../../../../store/selectors'

const useStyles = makeStyles((theme: Theme) => ({
  section: {
    minHeight: 64,
    paddingTop: theme.spacing(2),
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  address: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(3),
    '& :first-child': {
      paddingRight: theme.spacing(2),
    },
  },
  hash: {
    maxWidth: 185,
    overflow: 'hidden',
  },
  iconCopy: {
    padding: 0,
    color: theme.palette.primary.light,
    opacity: 0.6,
    paddingLeft: theme.spacing(1),
    '& :first-child': {
      padding: 0,
      fontSize: 18,
    },
  },
  description: {
    paddingTop: theme.spacing(3),
    fontSize: 14,
    opacity: 0.8,
  },
}))

export const FundAccount: FC<{
  onSuccess: () => void
  walletEnums: Array<{ label: string; value: string }>
}> = ({ walletEnums = [] }) => {
  const formMethods = useForm({
    mode: 'onChange',
  })
  const classes = useStyles()
  const { watch } = formMethods
  const wallets = useSelector(userSfWalletsSelector)
  const loading = useSelector(isLoadingUserWalletsListSelector)
  const defaultSelectedCurrency = walletEnums[0]?.value || ''
  const [selectedWallet, setSelectedWallet] = useState<ISfWallet | null>(
    wallets?.data?.items?.find((wallet) => wallet.currency === defaultSelectedCurrency) || null,
  )
  const isDisabledForm = !walletEnums.length

  const copyClipboard = useCallback(() => {
    if (selectedWallet?.address) {
      navigator.clipboard.writeText(selectedWallet?.address).then()
      toast(`Address copied to clipboard!`, { type: 'success' })
    }
  }, [selectedWallet?.address])

  useEffect(() => {
    const subscription = watch((value) => {
      setSelectedWallet(wallets?.data?.items?.find((wallet) => wallet.currency === value.cryptoCurrency) || null)
    })

    return () => subscription.unsubscribe()
  }, [wallets?.data?.items, watch])

  return (
    <FormProvider {...formMethods}>
      <Box>
        {loading && <Loader />}

        <Grid container className={classes.section}>
          {isDisabledForm ? (
            <Typography>Please add a wallet and go through the whitelisting process.</Typography>
          ) : (
            <>
              <Grid item xs={12}>
                <InputSelect
                  inputName="cryptoCurrency"
                  labelName={'Select Crypto Currency'}
                  width={450}
                  enums={walletEnums}
                  defaultValue={defaultSelectedCurrency}
                  disable={isDisabledForm}
                />
              </Grid>

              {!!selectedWallet && (
                <Grid item xs={12} padding={6} textAlign={'center'}>
                  <QRCode value={selectedWallet.address} size={180} />

                  <Grid className={classes.address}>
                    <Typography>Address:</Typography>
                    <Typography className={classes.hash}>
                      {selectedWallet.address.length > 16
                        ? `${selectedWallet.address.slice(0, 16)}...`
                        : selectedWallet.address}
                    </Typography>
                    <IconButton className={classes.iconCopy} onClick={copyClipboard}>
                      <span className="material-icons">{icons.COPY}</span>
                    </IconButton>
                  </Grid>
                  <Typography className={classes.description}>
                    For wiring instructions please contact your account rep!
                  </Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Box>
    </FormProvider>
  )
}
